"use client";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

interface SectionDescriptionProps {
    text?: string;
    className?: string;
    /** When false, always show the full text without Read More / Read Less. */
    collapsible?: boolean;
}

// Collapsed view shows this many full lines, ending with an ellipsis.
const COLLAPSED_CLASS = "line-clamp-2";

const isHtml = (text: string) => /<[a-z][\s\S]*>/i.test(text);

const SectionDescription: React.FC<SectionDescriptionProps> = ({ text, className = "", collapsible = true }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    // Start true so SSR renders the button immediately (prevents CLS);
    // useLayoutEffect corrects it synchronously for short content.
    const [isOverflowing, setIsOverflowing] = useState(collapsible);
    const contentRef = useRef<HTMLDivElement>(null);
    const isExpandedRef = useRef(isExpanded);
    isExpandedRef.current = isExpanded;

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el || !collapsible) return;
        setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [text, collapsible]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el || !collapsible) return;

        const ro = new ResizeObserver(() => {
            if (!isExpandedRef.current) {
                setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [text, collapsible]);

    if (!text) return null;

    const contentClass = `overflow-hidden [&_p]:inline [&_p]:m-0 ${isExpanded || !collapsible ? "" : COLLAPSED_CLASS}`;

    return (
        <div className={`text-[14px] leading-5 text-[#6B7280] ${className}`}>
            {isHtml(text) ? (
                <div ref={contentRef} className={contentClass} dangerouslySetInnerHTML={{ __html: text }} />
            ) : (
                <div ref={contentRef} className={contentClass}>{text}</div>
            )}

            {collapsible && (isOverflowing || isExpanded) && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-1 text-[#007AFF] font-medium focus:outline-none cursor-pointer"
                >
                    {isExpanded ? "Read Less" : "Read More"}
                </button>
            )}
        </div>
    );
};

export default SectionDescription;
